// ═══════════════════════════════════════════════════════════════════════════
// formRegister.jsx — Registro con GPS + Empresa + Asignación de Zona
// ═══════════════════════════════════════════════════════════════════════════
// Cambios respecto al original:
// 1. Carga politica de registro al inicio (getPoliticaRegistro)
// 2. Step "Ubicación" dinámico — solo aparece si politica.requiere_gps
// 3. Toggle "Soy empresa" + campo nombre_fantasia — si politica.requiere_nombre_fantasia
// 4. GPS: solicita ubicación, busca zonas, muestra sucursal asignada
// 5. Submit envía cli_gps_latitud, cli_gps_longitud, zona_id, nombre_fantasia
// ═══════════════════════════════════════════════════════════════════════════

import { useForm, Controller } from "react-hook-form";
import { useState, useEffect, useContext } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  TextField, Button, Typography, Box, Grid, MenuItem, FormControl, InputLabel,
  Select, FormHelperText, Alert, Switch, FormControlLabel, Paper, Step,
  StepLabel, Stepper, Avatar, CircularProgress, Chip,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveIcon from "@mui/icons-material/Save";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BusinessIcon from "@mui/icons-material/Business";
import { ClienteFormContext } from "../../../context/cliente/ClienteContext";
import { AuthContext } from "../../../context/authContext";
import { getTipoDocumentos } from "../../../services/TipoDocumentoServices";
import { getDataFiscal, validateDataClient } from "../../../services/ClientesServices";
import { getProvincias } from "../../../services/ProvinciaServices";
import { getDepartamentos } from "../../../services/DepartamentosServices";
import { getLocalidades } from "../../../services/LocalidadesServices";
import { getPoliticaRegistro, getZonasPorUbicacion } from "../../../services/ClientesZonasServices";
import { useNavigate, useSearchParams } from "react-router-dom";
import { validation } from "./helpers/validation";

const TIPO_IVA_OPTIONS = [
  { value: 1, label: "Responsable Inscripto" },
  { value: 5, label: "Monotributista" },
  { value: 4, label: "Consumidor Final" },
  { value: 2, label: "Exento" },
];

const filtrarTiposDoc = (cond, tipos) => {
  if (!cond || !tipos.length) return [];
  let f = [1, 5].includes(cond) ? tipos.filter(t => t.td_descripcion.toLowerCase() === "cuit")
    : cond === 4 ? tipos.filter(t => t.td_descripcion.toLowerCase() !== "cuit") : [...tipos];
  return f.filter(t => t.id !== 242);
};

const menuProps = { MenuProps: { PaperProps: { style: { maxHeight: 48 * 5.5 } } } };
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px", backgroundColor: "#f8fafc", transition: "all 0.18s ease",
    "& fieldset": { border: "1.5px solid #e2e8f0" }, "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "2px" }, "&.Mui-focused": { backgroundColor: "#fff" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem", "&.Mui-focused": { color: "#6366f1" } },
};

export const Form = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [tipoDocOptions, setTipoDocOptions] = useState([]);
  const [filteredTipoDocs, setFilteredTipoDocs] = useState([]);
  const [entregaDiferente, setEntregaDiferente] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [provincias, setProvincias] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [deptoEntrega, setDeptoEntrega] = useState([]);
  const [locEntrega, setLocEntrega] = useState([]);

  // GPS + política
  const [politica, setPolitica] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [zonaAsignada, setZonaAsignada] = useState(null);
  const [esEmpresa, setEsEmpresa] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get("email");
  const token = searchParams.get("token");
  const { clienteSelected } = useContext(ClienteFormContext);
  const { getMeData } = useContext(AuthContext);
  const { register, handleSubmit, control, watch, setValue, trigger, formState: { errors } } = useForm({ mode: "onChange", resolver: yupResolver(validation) });

  const condicionFiscal = watch("tipoiva_id");
  const provinciaId = watch("pcia_id");
  const departamentoId = watch("depto_id");
  const provinciaEntregaId = watch("cli_pcia_entrega");
  const deptoEntregaId = watch("cli_depto_entrega");

  useEffect(() => { if (!email || !token) navigate("/login"); }, [email, token, navigate]);
  useEffect(() => {
    getTipoDocumentos().then(setTipoDocOptions).catch(console.error);
    getProvincias().then(r => setProvincias(r || [])).catch(console.error);
    getPoliticaRegistro().then(setPolitica).catch(console.error);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const u = localStorage.getItem("user"); if (!u) return;
        const p = JSON.parse(u); const id = p.rol_id === 1 ? clienteSelected?.id : p.id; if (!id) return;
        const data = await getDataFiscal(id); if (!data) return;
        Object.entries(data).forEach(([k, v]) => setValue(k, v));
        if (data.cli_direccion_entrega || data.cli_pcia_entrega) setEntregaDiferente(true);
      } catch (e) { console.error(e); }
    })();
  }, [clienteSelected, setValue]);

  useEffect(() => { const f = filtrarTiposDoc(condicionFiscal, tipoDocOptions); if (!f.some(t => t.id === watch("td_id"))) setValue("td_id", ""); setFilteredTipoDocs(f); }, [condicionFiscal, tipoDocOptions]);
  useEffect(() => { if (![1, 5].includes(condicionFiscal)) { setValue("razon_social", ""); setValue("cli_cuit", ""); } }, [condicionFiscal]);
  useEffect(() => { if (!provinciaId) { setDepartamentos([]); return; } getDepartamentos(provinciaId).then(r => setDepartamentos(r || [])).catch(console.error); setValue("depto_id", ""); setValue("loc_id", ""); }, [provinciaId]);
  useEffect(() => { if (!departamentoId) { setLocalidades([]); return; } getLocalidades(departamentoId).then(r => setLocalidades(r || [])).catch(console.error); setValue("loc_id", ""); }, [departamentoId]);
  useEffect(() => { if (!provinciaEntregaId) { setDeptoEntrega([]); return; } getDepartamentos(provinciaEntregaId).then(r => setDeptoEntrega(r || [])).catch(console.error); setValue("cli_depto_entrega", ""); setValue("cli_loc_entrega", ""); }, [provinciaEntregaId]);
  useEffect(() => { if (!deptoEntregaId) { setLocEntrega([]); return; } getLocalidades(deptoEntregaId).then(r => setLocEntrega(r || [])).catch(console.error); setValue("cli_loc_entrega", ""); }, [deptoEntregaId]);

  // ── Steps dinámicos ──
  const STEPS = [
    { id: "datos", label: "Datos Personales" },
    { id: "direccion", label: "Dirección Legal" },
    ...(politica?.requiere_gps ? [{ id: "ubicacion", label: "Ubicación" }] : []),
    { id: "entrega", label: "Dir. de Entrega" },
  ];
  const CAMPOS = { datos: ["tipoiva_id", "td_id", "cli_cuit", "cli_tel"], direccion: ["cli_direccion", "cli_nro_calle", "cli_cp", "pcia_id", "depto_id", "loc_id"], ubicacion: [], entrega: [] };

  // ── GPS ──
  const solicitarGps = () => {
    if (!navigator.geolocation) { setGpsError("Tu navegador no soporta geolocalización."); return; }
    setGpsLoading(true); setGpsError(null); setZonaAsignada(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setGpsCoords(c);
        try {
          const zonas = await getZonasPorUbicacion(c.lat, c.lng);
          if (zonas?.length > 0) setZonaAsignada(zonas[0]);
          else if (!politica.permite_registro_sin_zona) setGpsError(politica.mensaje_fuera_zona || "Tu ubicación no está en zona de cobertura.");
        } catch { setGpsError("Error al buscar zonas."); }
        setGpsLoading(false);
      },
      (err) => { setGpsLoading(false); setGpsError(err.code === 1 ? "Permiso denegado. Activá la ubicación." : "No se pudo obtener tu ubicación."); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleNext = async () => {
    const campos = CAMPOS[STEPS[activeStep].id] || [];
    if (campos.length) { const ok = await trigger(campos); if (!ok) return; }
    if (STEPS[activeStep].id === 'ubicacion') {
      if (!gpsCoords) { setGpsError("Compartí tu ubicación para continuar."); return; }
      if (!zonaAsignada && !politica.permite_registro_sin_zona) return;
    }
    setActiveStep(p => p + 1);
  };

  const onSubmit = async (data) => {
    try {
      let fd = {
        ...data, cli_mail: email, token, email_validado: 1,
        ...(esEmpresa && data.nombre_fantasia && { nombre_fantasia: data.nombre_fantasia }),
        ...(gpsCoords && { cli_gps_latitud: String(gpsCoords.lat), cli_gps_longitud: String(gpsCoords.lng) }),
        ...(zonaAsignada && { zona_id: zonaAsignada.id }),
      };
      if (!entregaDiferente) ["cli_direccion_entrega", "cli_nro_calle_entrega", "cli_piso_entrega", "cli_dpto_entrega", "cli_cp_entrega", "cli_pcia_entrega", "cli_depto_entrega", "cli_loc_entrega", "cli_direccion_entrega_observ"].forEach(k => delete fd[k]);
      fd = Object.fromEntries(Object.entries(fd).filter(([_, v]) => v !== null && v !== undefined && v !== ""));
      await validateDataClient(fd);
      await getMeData();
      navigate("/");
    } catch { setErrorMessage("Ha ocurrido un error. Contactese con soporte."); }
  };

  const paso = STEPS[activeStep];
  const isLast = activeStep === STEPS.length - 1;

  const renderContent = () => {
    switch (paso?.id) {
      case "datos": return (
        <Grid container spacing={2}>
          {/* Tipo de cliente: Persona o Empresa */}
          <Grid item xs={12}>
            <FormControl fullWidth sx={inputSx}>
              <InputLabel>¿Cómo querés registrarte?</InputLabel>
              <Controller name="tipo_cliente" control={control}
                render={({ field }) => (
                  <Select
                    label="¿Cómo querés registrarte?"
                    value={field.value ?? ""}
                    onChange={e => {
                      field.onChange(e.target.value);
                      setEsEmpresa(e.target.value === 'empresa');
                    }}
                  >
                    <MenuItem value="persona">👤 Persona física</MenuItem>
                    <MenuItem value="empresa">🏢 Empresa / Negocio</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          {esEmpresa && (
            <Grid item xs={12}>
              <TextField
                fullWidth size="small"
                label="Nombre de Fantasía / Razón Social"
                {...register("razon_social")}
                placeholder="Ej: Distribuidora López, Kiosco El Sol..."
                sx={inputSx}
                helperText="Nombre con el que se identifica el negocio"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}><FormControl fullWidth error={!!errors.tipoiva_id} sx={inputSx}><InputLabel>Condición de IVA</InputLabel><Controller name="tipoiva_id" control={control} render={({ field }) => (<Select label="Condición de IVA" value={field.value ?? ""} onChange={e => field.onChange(Number(e.target.value))}>{TIPO_IVA_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}</Select>)} /><FormHelperText>{errors.tipoiva_id?.message}</FormHelperText></FormControl></Grid>
          <Grid item xs={12} sm={6}><FormControl fullWidth error={!!errors.td_id} sx={inputSx}><InputLabel>Tipo de Documento</InputLabel><Controller name="td_id" control={control} render={({ field }) => (<Select label="Tipo de Documento" value={field.value ?? ""} onChange={e => field.onChange(Number(e.target.value))}>{filteredTipoDocs.map(t => <MenuItem key={t.id} value={Number(t.id)}>{t.td_descripcion}</MenuItem>)}</Select>)} /><FormHelperText>{errors.td_id?.message}</FormHelperText></FormControl></Grid>
          <Grid item xs={12} sm={6}><Controller name="cli_cuit" control={control} render={({ field }) => (<TextField {...field} fullWidth size="small" label="Nro Documento" error={!!errors.cli_cuit} helperText={errors.cli_cuit?.message} sx={inputSx} />)} /></Grid>
          <Grid item xs={12} sm={6}><Controller name="cli_tel" control={control} render={({ field }) => (<TextField {...field} fullWidth size="small" label="Teléfono" error={!!errors.cli_tel} helperText={errors.cli_tel?.message} sx={inputSx} />)} /></Grid>
        </Grid>
      );

      case "direccion": return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Calle" {...register("cli_direccion")} error={!!errors.cli_direccion} helperText={errors.cli_direccion?.message} sx={inputSx} /></Grid>
          <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="Nro" {...register("cli_nro_calle")} error={!!errors.cli_nro_calle} helperText={errors.cli_nro_calle?.message} sx={inputSx} /></Grid>
          <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="Piso" {...register("cli_piso")} sx={inputSx} /></Grid>
          <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="Dpto" {...register("cli_dpto")} sx={inputSx} /></Grid>
          <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="CP" {...register("cli_cp")} error={!!errors.cli_cp} helperText={errors.cli_cp?.message} sx={inputSx} /></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Provincia" {...register("pcia_id")} value={watch("pcia_id") || ""} SelectProps={menuProps} error={!!errors.pcia_id} helperText={errors.pcia_id?.message} sx={inputSx}>{provincias.map(p => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Departamento" {...register("depto_id")} value={watch("depto_id") || ""} SelectProps={menuProps} disabled={!departamentos.length} error={!!errors.depto_id} helperText={errors.depto_id?.message} sx={inputSx}>{departamentos.map(d => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Localidad" {...register("loc_id")} value={watch("loc_id") || ""} SelectProps={menuProps} disabled={!localidades.length} error={!!errors.loc_id} helperText={errors.loc_id?.message} sx={inputSx}>{localidades.map(l => <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Observaciones" {...register("cli_direccion_observ")} sx={inputSx} /></Grid>
        </Grid>
      );

      case "ubicacion": return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, py: 2 }}>
          <Alert severity="info" sx={{ borderRadius: '12px', width: '100%' }}>Necesitamos tu ubicación para asignarte a la sucursal más cercana.</Alert>
          <Button onClick={solicitarGps} disabled={gpsLoading} variant={gpsCoords ? "outlined" : "contained"}
            startIcon={gpsLoading ? <CircularProgress size={18} color="inherit" /> : <MyLocationIcon />}
            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '0.95rem', px: 4, py: 1.5, ...(gpsCoords ? { borderColor: '#10b981', color: '#10b981' } : { bgcolor: '#6366f1', boxShadow: '0 4px 14px rgba(99,102,241,0.35)', '&:hover': { bgcolor: '#4f46e5' } }) }}>
            {gpsLoading ? 'Obteniendo...' : gpsCoords ? 'Actualizar ubicación' : 'Compartir mi ubicación'}
          </Button>
          {gpsError && <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ borderRadius: '12px', width: '100%' }}>{gpsError}</Alert>}
          {gpsCoords && (
            <Paper elevation={0} sx={{ width: '100%', p: 2.5, borderRadius: '14px', border: '1.5px solid #e2e8f0' }}>

              {zonaAsignada ? (
                <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#f0fdf4', border: '1.5px solid #86efac', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><StorefrontIcon sx={{ color: '#059669', fontSize: 22 }} /></Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>{zonaAsignada.sucursal?.nombre || 'Sucursal'}</Typography>
                  </Box>
                  <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
                </Box>
              ) : (
                <Alert severity={politica?.permite_registro_sin_zona ? "warning" : "error"} sx={{ borderRadius: '10px' }}>
                  {politica?.mensaje_fuera_zona || "No encontramos zona de entrega para tu ubicación."}
                  {politica?.permite_registro_sin_zona && <Typography sx={{ fontSize: '0.75rem', mt: 0.5 }}>Podés continuar igual.</Typography>}
                </Alert>
              )}
            </Paper>
          )}
        </Box>
      );

      case "entrega": return (
        <>
          <FormControlLabel sx={{ mb: 2 }} control={<Switch checked={entregaDiferente} onChange={e => setEntregaDiferente(e.target.checked)} />}
            label={<Typography variant="body2" fontWeight={500}>Dirección de entrega diferente a la legal</Typography>} />
          {!entregaDiferente && <Alert severity="info" sx={{ borderRadius: "10px" }}>Se usará la dirección legal.</Alert>}
          {entregaDiferente && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Calle" {...register("cli_direccion_entrega")} sx={inputSx} /></Grid>
              <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="Nro" {...register("cli_nro_calle_entrega")} sx={inputSx} /></Grid>
              <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="Piso" {...register("cli_piso_entrega")} sx={inputSx} /></Grid>
              <Grid item xs={4} sm={2}><TextField fullWidth size="small" label="Dpto" {...register("cli_dpto_entrega")} sx={inputSx} /></Grid>
              <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="CP" {...register("cli_cp_entrega")} sx={inputSx} /></Grid>
              <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Provincia" {...register("cli_pcia_entrega")} value={watch("cli_pcia_entrega") || ""} SelectProps={menuProps} sx={inputSx}>{provincias.map(p => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Departamento" {...register("cli_depto_entrega")} value={watch("cli_depto_entrega") || ""} SelectProps={menuProps} disabled={!deptoEntrega.length} sx={inputSx}>{deptoEntrega.map(d => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} sm={4}><TextField select fullWidth size="small" label="Localidad" {...register("cli_loc_entrega")} value={watch("cli_loc_entrega") || ""} SelectProps={menuProps} disabled={!locEntrega.length} sx={inputSx}>{locEntrega.map(l => <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Observaciones" {...register("cli_direccion_entrega_observ")} sx={inputSx} /></Grid>
            </Grid>
          )}
        </>
      );
      default: return null;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", alignItems: "flex-start", justifyContent: "center", py: { xs: 3, md: 6 }, px: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 680 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
          <Avatar sx={{ width: 52, height: 52, mb: 1.5, background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}><AccountCircleIcon sx={{ fontSize: 28 }} /></Avatar>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.3rem", sm: "1.5rem" }, color: "#1e293b" }}>Completá tus datos</Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", mt: 0.5 }}>Necesitamos algunos datos para activar tu cuenta</Typography>
        </Box>
        {errorMessage && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{errorMessage}</Alert>}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((s, i) => (<Step key={s.id} completed={i < activeStep}><StepLabel sx={{ "& .MuiStepLabel-label": { fontSize: "0.72rem", fontWeight: activeStep === i ? 700 : 400 }, "& .MuiStepIcon-root.Mui-active": { color: "#6366f1" }, "& .MuiStepIcon-root.Mui-completed": { color: "#6366f1" } }}>{s.label}</StepLabel></Step>))}
        </Stepper>
        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: "16px", border: "1.5px solid #e2e8f0", minHeight: 280 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#6366f1", mb: 2.5 }}>{paso?.label}</Typography>
          {renderContent()}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button variant="outlined" onClick={() => setActiveStep(p => p - 1)} disabled={activeStep === 0} startIcon={<ArrowBackIcon />} sx={{ borderRadius: "10px", textTransform: "none", borderColor: "#e2e8f0", color: "#64748b" }}>Anterior</Button>
            {isLast
              ? <Button variant="contained" onClick={handleSubmit(onSubmit)} startIcon={<SaveIcon />} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#6366f1", boxShadow: "0 4px 14px rgba(99,102,241,0.35)", "&:hover": { bgcolor: "#4f46e5" } }}>Guardar</Button>
              : <Button variant="contained" onClick={handleNext} endIcon={<ArrowForwardIcon />} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#6366f1", boxShadow: "0 4px 14px rgba(99,102,241,0.35)", "&:hover": { bgcolor: "#4f46e5" } }}>Siguiente</Button>
            }
          </Box>
        </Paper>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
          {STEPS.map((_, i) => <Box key={i} sx={{ width: i === activeStep ? 20 : 8, height: 8, borderRadius: "4px", bgcolor: i === activeStep ? "#6366f1" : "#e2e8f0", transition: "all 0.25s ease" }} />)}
        </Box>
      </Box>
    </Box>
  );
};